// ============================================================
// AdminChatAiPage — M10.7 (PRD §6.1.H R-P0-H1..H8 + §7.3 AC-CHAT-1..4)
// ============================================================
// Admin chat tay-đôi với AI để kiểm tra/huấn luyện KB + dặn dò AI.
//
// Layout:
//   ┌─────────────┬──────────────────────────────────┐
//   │  Sessions   │  Chat pane (messages)            │
//   │  - new      │  ┌──────────────────────────┐   │
//   │  - list     │  │ user: ...                │   │
//   │  - rename   │  │ ai: markdown... [💾 Lưu] │   │
//   │  - delete   │  └──────────────────────────┘   │
//   │             │  [textarea] [🖼] [Gửi]          │
//   └─────────────┴──────────────────────────────────┘
//
// Luồng:
//   1. Load capabilities (provider/model/supports_multimodal/enabled/has_api_key)
//   2. Load sessions (sidebar). Nếu chưa có session → state = no-session.
//   3. Click session → fetch messages → render.
//   4. Gửi message (Enter/click) → POST /messages → append user + ai message.
//   5. Ở mỗi AI message có nút "💾 Lưu dặn dò" → modal nhập tên + scope → POST save-dan-do.
//   6. Upload ảnh (max 5) — chỉ hiện nếu supports_multimodal. Paths trả từ BE (TODO M10.8?).
//      Ở M10.7 tạm thời hiển thị input file nhưng đưa nội dung base64 vào attachment_paths.
//      (Chi tiết upload BE đã có stub — xem chatAiService — FE chỉ gửi path string.)
// ============================================================

import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { useAuthStore } from '../lib/auth.js';
import { api } from '../lib/api.js';
import { showConfirm, showPrompt } from '../lib/dialogs.js';

marked.setOptions({ breaks: true, gfm: true });

// Sanitize markdown HTML (reuse pattern từ AskPage — a, table cho prompts).
const SAFE_TAGS = [
  'p', 'br', 'strong', 'em', 'u', 's', 'code', 'pre',
  'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'blockquote', 'hr', 'span', 'div', 'a',
  'table', 'thead', 'tbody', 'tr', 'th', 'td',
];
function sanitizeMd(html) {
  if (!html) return '';
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: SAFE_TAGS,
    ALLOWED_ATTR: ['class', 'href', 'title'],
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'style', 'srcset', 'src'],
    ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
  });
}

function mdToSafeHtml(md) {
  try { return sanitizeMd(marked.parse(String(md || ''))); }
  catch { return sanitizeMd(String(md || '').replace(/</g, '&lt;')); }
}

function fmtTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('vi-VN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' });
  } catch { return iso; }
}

/**
 * M27 (2026-04-24) — đọc File ảnh → resize max maxDim × maxDim giữ tỷ lệ,
 * encode base64 (JPEG quality q, trừ PNG/GIF/WebP giữ nguyên).
 * Return base64 string PURE (không có prefix "data:...;base64,").
 *
 * Resize qua canvas 2D. Chạy trong browser không cần thư viện ngoài.
 * Fail khi:
 *   - File quá to không load được (browser throw)
 *   - Non-image mime
 */
async function fileToResizedBase64(file, maxDim = 1600, q = 0.82) {
  const keepFormats = new Set(['image/png', 'image/gif', 'image/webp']);
  const outMime = keepFormats.has(file.type) ? file.type : 'image/jpeg';

  // Load thành image element
  const url = URL.createObjectURL(file);
  const img = await new Promise((res, rej) => {
    const im = new Image();
    im.onload = () => res(im);
    im.onerror = () => rej(new Error('Không đọc được ảnh'));
    im.src = url;
  });

  // Tính kích thước target giữ tỉ lệ
  let { width, height } = img;
  if (width > maxDim || height > maxDim) {
    const r = Math.min(maxDim / width, maxDim / height);
    width = Math.round(width * r);
    height = Math.round(height * r);
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, width, height);
  URL.revokeObjectURL(url);

  // Export → dataURL → strip prefix
  const dataUrl = canvas.toDataURL(outMime, q);
  const base64 = dataUrl.replace(/^data:[^;]+;base64,/, '');
  return base64;
}

export default function AdminChatAiPage() {
  const nav = useNavigate();
  const { user } = useAuthStore();
  // M146: manager với quyền 'chat_ai_admin' cũng truy cập được
  const isAdmin = (function () {
    if (user?.role === 'admin') return true;
    if (user?.role === 'manager' && Array.isArray(user?.permissions)) return user.permissions.includes('chat_ai_admin');
    return false;
  })();

  // ----- State chung -----
  const [caps, setCaps] = useState(null);           // capabilities
  const [capsErr, setCapsErr] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);      // { session, messages }
  const [detailLoading, setDetailLoading] = useState(false);

  // ----- Compose -----
  const [content, setContent] = useState('');
  const [attachments, setAttachments] = useState([]); // Array<File>
  const [sending, setSending] = useState(false);

  // ----- Save dặn dò modal -----
  const [saveModal, setSaveModal] = useState(null); // { messageId, suggestedName }

  const chatEndRef = useRef(null);

  useEffect(() => {
    if (!isAdmin) {
      toast.error('Chỉ admin mới dùng Chat AI');
      nav('/', { replace: true });
    }
  }, [isAdmin, nav]);

  // Load capabilities + sessions 1 lần
  useEffect(() => {
    if (!isAdmin) return;
    api.chatAiCapabilities()
      .then(setCaps)
      .catch(err => setCapsErr(err.data?.error || err.message || 'Không lấy được capabilities'));
    refreshSessions();
  }, [isAdmin]);

  // Auto scroll khi có message mới
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [detail?.messages?.length]);

  async function refreshSessions() {
    setSessionsLoading(true);
    try {
      const res = await api.chatAiListSessions();
      setSessions(res.sessions || []);
    } catch (err) {
      toast.error(err.data?.error || err.message || 'Không tải được sessions');
    } finally {
      setSessionsLoading(false);
    }
  }

  async function loadSession(id) {
    setSelectedId(id);
    setDetailLoading(true);
    try {
      const res = await api.chatAiGetSession(id);
      setDetail(res);
    } catch (err) {
      toast.error(err.data?.error || err.message || 'Không tải được session');
      setDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }

  async function createNewSession() {
    try {
      const res = await api.chatAiCreateSession({});
      await refreshSessions();
      await loadSession(res.id);
      toast.success('Đã tạo session mới');
    } catch (err) {
      toast.error(err.data?.error || err.message || 'Không tạo được session');
    }
  }

  async function renameSession(id, currentTitle) {
    const next = await showPrompt({
      title: 'Đổi tên session',
      message: 'Nhập tên mới cho session:',
      defaultValue: currentTitle || '',
      placeholder: 'Tên session',
    });
    if (!next || next.trim() === '' || next.trim() === currentTitle) return;
    try {
      await api.chatAiRenameSession(id, next.trim());
      await refreshSessions();
      if (selectedId === id) await loadSession(id);
      toast.success('Đã đổi tên');
    } catch (err) {
      toast.error(err.data?.error || err.message || 'Đổi tên thất bại');
    }
  }

  async function deleteSession(id) {
    const ok = await showConfirm({
      title: 'Xoá session',
      message: 'Xoá session này? Toàn bộ messages sẽ bị xoá vĩnh viễn.',
      confirmText: 'Xoá',
      dangerous: true,
    });
    if (!ok) return;
    try {
      await api.chatAiDeleteSession(id);
      if (selectedId === id) { setSelectedId(null); setDetail(null); }
      await refreshSessions();
      toast.success('Đã xoá session');
    } catch (err) {
      toast.error(err.data?.error || err.message || 'Xoá thất bại');
    }
  }

  async function sendMessage() {
    const text = content.trim();
    if (!text) { toast.error('Vui lòng nhập nội dung'); return; }
    if (!selectedId) { toast.error('Chưa chọn session'); return; }
    if (text.length > 10000) { toast.error('Nội dung quá dài (tối đa 10000)'); return; }

    if (attachments.length > 0 && !caps?.supports_multimodal) {
      toast.error('Provider/model hiện tại không hỗ trợ ảnh. Bỏ file đính kèm hoặc đổi provider ở Cài đặt AI.');
      return;
    }

    // M27 (2026-04-24): convert File[] → {mime, data_base64} resize max 1600×1600
    // JPEG q=0.82 để ≤ ~1.5MB base64/ảnh, 5 ảnh ~7.5MB ≤ 25MB body limit.
    let attachmentsPayload = [];
    if (attachments.length > 0) {
      try {
        attachmentsPayload = await Promise.all(
          attachments.map(async (f) => ({
            mime: f.type === 'image/png' || f.type === 'image/gif' || f.type === 'image/webp'
              ? f.type
              : 'image/jpeg',
            data_base64: await fileToResizedBase64(f, 1600, 0.82),
          }))
        );
      } catch (err) {
        toast.error('Lỗi đọc/nén ảnh: ' + (err?.message || 'unknown'));
        return;
      }
    }

    setSending(true);
    try {
      const res = await api.chatAiSendMessage(selectedId, {
        content: text,
        attachments: attachmentsPayload,
      });
      // Append user + ai message vào detail
      // I5 (2026-04-24): attach debug info vào ai_message để render prompt/KB
      const aiMsgWithDebug = { ...res.ai_message, _debug: res.debug };
      setDetail(prev => prev ? {
        ...prev,
        messages: [...(prev.messages || []), res.user_message, aiMsgWithDebug],
      } : prev);
      setContent('');
      setAttachments([]);
      await refreshSessions(); // cập nhật updated_at trong sidebar
      const cost = res.debug?.cost_vnd ?? 0;
      toast.success(`AI trả lời ✓ (${cost}đ)`);
    } catch (err) {
      const msg = err.data?.error || err.message || 'Gửi thất bại';
      toast.error(msg);
    } finally {
      setSending(false);
    }
  }

  function openSaveDanDo(message) {
    // Gợi ý tên từ 40 ký tự đầu của AI response
    const firstLine = (message.content || '').split('\n')[0].trim();
    const suggested = firstLine.length > 40 ? firstLine.slice(0, 40) + '…' : firstLine;
    setSaveModal({ messageId: message.id, suggestedName: suggested });
  }

  const selectedSession = sessions.find(s => s.id === selectedId);
  const providerLabel = caps
    ? `${caps.provider || '?'} / ${caps.model || '?'}` + (caps.supports_multimodal ? ' · ảnh OK' : ' · chỉ text')
    : '…';
  const providerWarn = caps && !caps.enabled
    ? 'Provider chưa được bật — đến Cài đặt AI để kích hoạt'
    : caps && !caps.has_api_key
      ? 'Provider chưa có API key — đến Cài đặt AI để nhập'
      : null;

  return (
    <div className="min-h-full p-3 md:p-4">
      <div className="max-w-7xl mx-auto">
        {/* ===== HEADER ===== M148c: mobile-first stack */}
        <header className="mb-4">
          <Link
            to="/admin"
            className="text-sm text-slate-500 hover:text-slate-700"
            data-testid="chatai-back"
          >
            ← Trang admin
          </Link>
          <div className="mt-1 flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <h1 className="text-lg md:text-xl font-semibold text-brand-700">Chat AI (Admin)</h1>
              <p className="text-xs text-slate-500 mt-0.5">
                Trao đổi với AI để test KB · Dặn dò AI · Lưu câu trả lời hay làm prompt
              </p>
            </div>
          <div className="text-left md:text-right shrink-0">
            <div className="text-xs text-slate-500">Provider:</div>
            <div className="text-sm font-mono text-slate-700">{providerLabel}</div>
            {providerWarn && (
              <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5 mt-1">
                ⚠ {providerWarn}
              </div>
            )}
            {capsErr && (
              <div className="text-[11px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-0.5 mt-1">
                ⚠ {capsErr}
              </div>
            )}
          </div>
          </div>
        </header>

        {/* ===== LAYOUT 2 CỘT ===== */}
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4">
          {/* ===== SIDEBAR ===== */}
          <aside className="bg-white rounded-2xl shadow-sm border border-slate-200 p-3 md:sticky md:top-4 md:max-h-[calc(100vh-8rem)] md:overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-semibold text-sm text-slate-700">Sessions</h2>
              <button
                type="button"
                onClick={createNewSession}
                data-testid="chatai-new-session"
                className="px-2 py-1 rounded-md bg-brand-600 hover:bg-brand-700 text-white text-xs font-semibold"
              >
                ➕ Mới
              </button>
            </div>
            {sessionsLoading && <div className="text-xs text-slate-400">Đang tải…</div>}
            {!sessionsLoading && sessions.length === 0 && (
              <div className="text-xs text-slate-400 italic py-4 text-center">
                Chưa có session nào — bấm "➕ Mới" để bắt đầu
              </div>
            )}
            <ul className="space-y-1">
              {sessions.map(s => (
                <li key={s.id}>
                  <div
                    className={`group rounded-lg px-2 py-1.5 border transition cursor-pointer ${
                      selectedId === s.id
                        ? 'bg-brand-50 border-brand-300 text-brand-800'
                        : 'bg-white border-transparent hover:bg-slate-50 hover:border-slate-200 text-slate-700'
                    }`}
                    onClick={() => loadSession(s.id)}
                    data-testid={`chatai-session-${s.id}`}
                  >
                    <div className="flex items-center justify-between gap-1">
                      <span className="text-sm font-medium truncate flex-1">
                        {s.title || `Session #${s.id}`}
                      </span>
                      <div className="hidden group-hover:flex items-center gap-0.5 shrink-0">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); renameSession(s.id, s.title); }}
                          title="Đổi tên"
                          className="p-1 text-xs hover:bg-white rounded"
                          data-testid={`chatai-rename-${s.id}`}
                        >✏️</button>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); deleteSession(s.id); }}
                          title="Xoá"
                          className="p-1 text-xs hover:bg-white rounded"
                          data-testid={`chatai-delete-${s.id}`}
                        >🗑</button>
                      </div>
                    </div>
                    <div className="text-[10px] text-slate-400 mt-0.5">
                      {fmtTime(s.updated_at)} · {s.message_count ?? 0} msg
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </aside>

          {/* ===== CHAT PANE ===== */}
          <main className="bg-white rounded-2xl shadow-sm border border-slate-200 flex flex-col overflow-hidden" style={{ height: 'calc(100vh - 10rem)' }}>
            {!selectedId && (
              <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">
                <div className="text-center">
                  <div className="text-4xl mb-2 select-none">💬</div>
                  <div>Chọn một session ở bên trái hoặc tạo session mới</div>
                </div>
              </div>
            )}

            {selectedId && detailLoading && (
              <div className="flex-1 flex items-center justify-center text-slate-400 text-sm">
                Đang tải session…
              </div>
            )}

            {selectedId && !detailLoading && detail && (
              <>
                {/* Header session */}
                <div className="p-3 border-b border-slate-100">
                  <div className="text-sm font-semibold text-slate-800">
                    {selectedSession?.title || `Session #${selectedId}`}
                  </div>
                  <div className="text-[11px] text-slate-400">
                    Tạo: {fmtTime(detail.session?.created_at)} · Cập nhật: {fmtTime(detail.session?.updated_at)}
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-4" data-testid="chatai-messages">
                  {(!detail.messages || detail.messages.length === 0) && (
                    <div className="text-sm text-slate-400 italic text-center py-8">
                      Chưa có tin nhắn — gõ câu hỏi bên dưới để bắt đầu
                    </div>
                  )}
                  {(detail.messages || []).map(m => (
                    <MessageBubble
                      key={m.id}
                      message={m}
                      onSaveDanDo={() => openSaveDanDo(m)}
                    />
                  ))}
                  <div ref={chatEndRef} />
                </div>

                {/* Composer */}
                <div className="border-t border-slate-100 p-3">
                  {attachments.length > 0 && (
                    <div className="mb-2 flex flex-wrap gap-1">
                      {attachments.map((f, i) => (
                        <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-slate-100 text-xs text-slate-700">
                          🖼 {f.name.length > 20 ? f.name.slice(0, 20) + '…' : f.name}
                          <button
                            type="button"
                            onClick={() => setAttachments(a => a.filter((_, idx) => idx !== i))}
                            className="text-slate-400 hover:text-rose-600"
                            title="Bỏ file"
                          >✕</button>
                        </span>
                      ))}
                    </div>
                  )}
                  <textarea
                    value={content}
                    onChange={e => setContent(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                        e.preventDefault();
                        if (!sending && content.trim()) sendMessage();
                      }
                    }}
                    placeholder="Gõ câu hỏi cho AI... (Enter gửi, Shift+Enter xuống dòng)"
                    maxLength={10000}
                    disabled={sending}
                    className="w-full min-h-[70px] rounded-lg border border-slate-300 focus:border-brand-500 focus:ring focus:ring-brand-200 focus:outline-none p-2.5 text-sm"
                    data-testid="chatai-input"
                  />
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center gap-2">
                      {caps?.supports_multimodal && (
                        <>
                          <input
                            type="file"
                            accept="image/*"
                            multiple
                            id="chatai-upload"
                            className="hidden"
                            onChange={(e) => {
                              const files = Array.from(e.target.files || []).slice(0, 5 - attachments.length);
                              if (files.length === 0) return;
                              setAttachments(a => [...a, ...files].slice(0, 5));
                              e.target.value = ''; // reset để upload lại file cùng tên
                            }}
                          />
                          <label
                            htmlFor="chatai-upload"
                            className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-50 hover:bg-slate-100 border border-slate-200 text-xs text-slate-600 cursor-pointer"
                            data-testid="chatai-upload-btn"
                            title="Đính kèm ảnh (tối đa 5)"
                          >🖼 Ảnh ({attachments.length}/5)</label>
                        </>
                      )}
                      <VoiceInputButton
                        onAppend={(text) => setContent(c => (c ? c + ' ' : '') + text)}
                        disabled={sending}
                      />
                      <span className="text-[11px] text-slate-400">
                        {content.length}/10000
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={sendMessage}
                      disabled={sending || !content.trim()}
                      data-testid="chatai-send-btn"
                      className="px-4 py-1.5 rounded-lg bg-brand-600 hover:bg-brand-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-sm font-semibold shadow-sm"
                    >
                      {sending ? 'Đang gửi…' : '📤 Gửi'}
                    </button>
                  </div>
                </div>
              </>
            )}
          </main>
        </div>
      </div>

      {/* ===== SAVE DẶN DÒ MODAL ===== */}
      {saveModal && (
        <SaveDanDoModal
          messageId={saveModal.messageId}
          suggestedName={saveModal.suggestedName}
          onClose={() => setSaveModal(null)}
          onSaved={() => {
            setSaveModal(null);
            toast.success('Đã lưu dặn dò — AI sẽ tham khảo lần sau');
          }}
        />
      )}
    </div>
  );
}

// =====================================================================
// MessageBubble
// =====================================================================
function MessageBubble({ message, onSaveDanDo }) {
  const isUser = message.role === 'user';
  const isAssistant = message.role === 'assistant';
  const isSystem = message.role === 'system';

  // System messages: hiện nhỏ ở giữa
  if (isSystem) {
    return (
      <div className="flex justify-center">
        <div className="text-[11px] text-slate-400 italic bg-slate-50 border border-slate-200 rounded px-2 py-1 max-w-xl">
          {message.content}
        </div>
      </div>
    );
  }

  const html = isAssistant ? mdToSafeHtml(message.content) : null;

  return (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[85%] md:max-w-[75%]`}>
        <div className={`flex items-center gap-2 mb-1 text-[11px] text-slate-400 ${isUser ? 'justify-end' : 'justify-start'}`}>
          <span>{isUser ? '👤 Admin' : '🤖 AI'}</span>
          <span>·</span>
          <span>{fmtTime(message.created_at)}</span>
          {message.tokens_out != null && (
            <>
              <span>·</span>
              <span className="font-mono">{message.tokens_out} tok</span>
            </>
          )}
          {message.cost_vnd != null && message.cost_vnd > 0 && (
            <>
              <span>·</span>
              <span className="font-mono">{message.cost_vnd}đ</span>
            </>
          )}
        </div>
        <div
          className={`rounded-2xl px-3 py-2 text-sm ${
            isUser
              ? 'bg-brand-600 text-white shadow-sm'
              : 'bg-slate-50 border border-slate-200 text-slate-800'
          }`}
          data-testid={`chatai-msg-${message.id}`}
        >
          {isAssistant
            ? <div className="markdown-body" dangerouslySetInnerHTML={{ __html: html }} />
            : <div className="whitespace-pre-wrap">{message.content}</div>
          }
          {/* Attachment indicator */}
          {Array.isArray(message.attachments) && message.attachments.length > 0 && (
            <div className={`mt-2 text-[11px] ${isUser ? 'text-brand-100' : 'text-slate-500'}`}>
              🖼 Đính kèm: {message.attachments.length} ảnh
            </div>
          )}
          {/* I5 (2026-04-24): Debug info — AI đã dùng prompt/KB nào */}
          {isAssistant && message._debug && (
            (message._debug.prompt_names?.length > 0 || message._debug.kb_titles?.length > 0) && (
              <div className="mt-2 pt-2 border-t border-slate-200 text-[10px] text-slate-400 flex flex-wrap gap-x-3 gap-y-0.5">
                {message._debug.prompt_names?.length > 0 && (
                  <span>💡 Dặn dò: <strong className="text-slate-600">{message._debug.prompt_names.join(', ')}</strong></span>
                )}
                {message._debug.kb_titles?.length > 0 && (
                  <span>📚 KB: <strong className="text-slate-600">{message._debug.kb_titles.join(', ')}</strong></span>
                )}
              </div>
            )
          )}
        </div>
        {/* AI message actions */}
        {isAssistant && onSaveDanDo && (
          <div className="mt-1.5 flex items-center gap-1">
            <button
              type="button"
              onClick={onSaveDanDo}
              data-testid={`chatai-save-dan-do-${message.id}`}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 font-medium"
              title="Lưu câu trả lời này làm prompt hệ thống (dặn dò AI)"
            >
              💾 Lưu dặn dò
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// =====================================================================
// SaveDanDoModal
// =====================================================================
function SaveDanDoModal({ messageId, suggestedName, onClose, onSaved }) {
  const [name, setName] = useState(suggestedName || '');
  const [scope, setScope] = useState('both');
  const [busy, setBusy] = useState(false);

  async function submit() {
    const n = name.trim();
    if (n.length < 3) { toast.error('Tên phải ít nhất 3 ký tự'); return; }
    if (n.length > 200) { toast.error('Tên dài tối đa 200 ký tự'); return; }
    setBusy(true);
    try {
      await api.chatAiSaveDanDo({
        message_id: messageId,
        name: n,
        scope,
      });
      onSaved?.();
    } catch (err) {
      toast.error(err.data?.error || err.message || 'Lưu thất bại');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="chatai-save-modal"
    >
      <div
        className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="text-3xl select-none">💾</div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-slate-800">Lưu dặn dò AI</h2>
            <p className="mt-1 text-xs text-slate-600">
              Nội dung câu trả lời AI sẽ được lưu làm <strong>prompt hệ thống</strong>.
              Lần sau AI sẽ tự tham khảo nó khi trả lời (tuỳ <em>scope</em> bên dưới).
            </p>
          </div>
        </div>

        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Tên dặn dò (3–200 ký tự)
            </label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              maxLength={200}
              className="w-full rounded-lg border border-slate-300 focus:border-brand-500 focus:ring focus:ring-brand-200 focus:outline-none p-2 text-sm"
              data-testid="chatai-save-name"
              disabled={busy}
            />
            <div className="text-[11px] text-slate-400 mt-1">{name.length}/200</div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">Áp dụng cho</label>
            <div className="flex gap-2 text-xs">
              {[
                { v: 'both',    l: 'Cả hai (Hỏi đáp + Chat AI)' },
                { v: 'ask_ai',  l: 'Chỉ Hỏi đáp (NV)' },
                { v: 'chat_ai', l: 'Chỉ Chat AI (admin)' },
              ].map(opt => (
                <label
                  key={opt.v}
                  className={`flex items-center gap-1 px-2 py-1 rounded border cursor-pointer ${
                    scope === opt.v ? 'bg-brand-50 border-brand-300 text-brand-800' : 'bg-white border-slate-200 text-slate-600'
                  }`}
                >
                  <input
                    type="radio"
                    name="scope"
                    value={opt.v}
                    checked={scope === opt.v}
                    onChange={() => setScope(opt.v)}
                    disabled={busy}
                    className="accent-brand-600"
                  />
                  <span>{opt.l}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button
            onClick={onClose}
            disabled={busy}
            data-testid="chatai-save-cancel"
            className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium"
          >Huỷ</button>
          <button
            onClick={submit}
            disabled={busy || name.trim().length < 3}
            data-testid="chatai-save-confirm"
            className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white text-sm font-semibold"
          >
            {busy ? 'Đang lưu…' : '💾 Lưu'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// R-P1-8: Voice input — Web Speech API (vi-VN)
// Ẩn nút nếu browser không hỗ trợ (Safari cũ, Firefox). Chrome/Edge OK.
// ============================================================
function VoiceInputButton({ onAppend, disabled }) {
  const [listening, setListening] = useState(false);
  const [supported, setSupported] = useState(false);
  const recognitionRef = useRef(null);

  useEffect(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      setSupported(false);
      return;
    }
    setSupported(true);
    const rec = new SR();
    rec.lang = 'vi-VN';
    rec.interimResults = false;
    rec.continuous = false;
    rec.maxAlternatives = 1;

    rec.onresult = (ev) => {
      const transcript = Array.from(ev.results)
        .map(r => r[0]?.transcript || '')
        .join(' ')
        .trim();
      if (transcript) onAppend(transcript);
    };
    rec.onerror = (ev) => {
      // Common errors: 'no-speech', 'not-allowed', 'aborted'
      if (ev.error && ev.error !== 'aborted') {
        toast.error(`Mic lỗi: ${ev.error === 'not-allowed' ? 'Bị chặn quyền — cho phép mic trong browser' : ev.error}`);
      }
      setListening(false);
    };
    rec.onend = () => setListening(false);

    recognitionRef.current = rec;
    return () => {
      try { rec.abort(); } catch { /* noop */ }
    };
  }, [onAppend]);

  if (!supported) return null;

  function toggle() {
    const rec = recognitionRef.current;
    if (!rec) return;
    if (listening) {
      try { rec.stop(); } catch { /* noop */ }
      setListening(false);
    } else {
      try {
        rec.start();
        setListening(true);
      } catch (err) {
        toast.error(`Không bật được mic: ${err.message || err}`);
        setListening(false);
      }
    }
  }

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={disabled}
      title={listening ? 'Đang nghe… click để dừng' : 'Nói để nhập (tiếng Việt)'}
      data-testid="chatai-voice-btn"
      className={`inline-flex items-center gap-1 px-2 py-1 rounded border text-xs font-medium transition ${
        listening
          ? 'bg-rose-50 border-rose-300 text-rose-700 animate-pulse'
          : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100'
      } disabled:opacity-40 disabled:cursor-not-allowed`}
    >
      {listening ? '⏺ Đang nghe…' : '🎤 Nói'}
    </button>
  );
}
